/**
 * 相机：世界坐标 ↔ 屏幕坐标，含平移、缩放与边缘滚动。
 *
 * 相机完全属于渲染层，不进入模拟。两个玩家看向地图的不同角落
 * 不会、也不允许影响任何模拟状态。
 */

import { WORLD_MIN_X, WORLD_MAX_X, WORLD_MIN_Y, WORLD_MAX_Y } from './iso.ts';

export class Camera {
  /** 视口中心对应的世界坐标 */
  x = 0;
  y = 0;
  zoom = 1;

  viewW = 800;
  viewH = 600;

  private targetZoom = 1;
  private panVX = 0;
  private panVY = 0;

  static readonly MIN_ZOOM = 0.45;
  static readonly MAX_ZOOM = 1.8;

  resize(w: number, h: number): void {
    this.viewW = w;
    this.viewH = h;
    this.clamp();
  }

  centerOn(wx: number, wy: number): void {
    this.x = wx;
    this.y = wy;
    this.clamp();
  }

  /** 键盘/边缘滚动的速度输入（像素/秒，屏幕空间） */
  setPan(vx: number, vy: number): void {
    this.panVX = vx;
    this.panVY = vy;
  }

  panBy(dxScreen: number, dyScreen: number): void {
    this.x -= dxScreen / this.zoom;
    this.y -= dyScreen / this.zoom;
    this.clamp();
  }

  /** 以屏幕上某点为锚心缩放，保证「滚轮对准哪里就放大哪里」 */
  zoomAt(delta: number, screenX: number, screenY: number): void {
    const before = this.screenToWorld(screenX, screenY);
    this.targetZoom = Math.max(
      Camera.MIN_ZOOM,
      Math.min(Camera.MAX_ZOOM, this.targetZoom * Math.pow(1.0015, -delta)),
    );
    this.zoom = this.targetZoom;
    const after = this.screenToWorld(screenX, screenY);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
    this.clamp();
  }

  update(dtSec: number): void {
    if (this.panVX !== 0 || this.panVY !== 0) {
      this.x += (this.panVX * dtSec) / this.zoom;
      this.y += (this.panVY * dtSec) / this.zoom;
      this.clamp();
    }
  }

  private clamp(): void {
    // 留出半个视口的余量，允许把地图边缘拖到屏幕中央
    const marginX = this.viewW / (2 * this.zoom);
    const marginY = this.viewH / (2 * this.zoom);
    this.x = Math.max(WORLD_MIN_X - marginX * 0.5, Math.min(WORLD_MAX_X + marginX * 0.5, this.x));
    this.y = Math.max(WORLD_MIN_Y - marginY * 0.5, Math.min(WORLD_MAX_Y + marginY * 0.5, this.y));
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return {
      x: (sx - this.viewW / 2) / this.zoom + this.x,
      y: (sy - this.viewH / 2) / this.zoom + this.y,
    };
  }

  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return {
      x: (wx - this.x) * this.zoom + this.viewW / 2,
      y: (wy - this.y) * this.zoom + this.viewH / 2,
    };
  }

  /** 供舞台容器使用的变换 */
  get stageX(): number {
    return this.viewW / 2 - this.x * this.zoom;
  }

  get stageY(): number {
    return this.viewH / 2 - this.y * this.zoom;
  }
}
